import React from 'react';
import Link from '@amzn/awsui-components-console/link';
import { BindleAccessLink } from 'src/common/utils/bindleLinks';
import { TranslateFn, useTranslation } from 'src/i18n/shim/useTranslation';

interface BindleAccessLinkProps {
  /**
   * The link already resolved by `resolveBindleAccessLink`.
   *
   * Resolved by the caller because the caller has to know whether a link exists
   * before it renders — it picks between the with-link layout and the
   * message-only one. This component only renders what it is handed, so the two
   * answers cannot disagree.
   */
  readonly link: BindleAccessLink;
}

/**
 * The link text offered for a denied namespace.
 *
 * Permitted UI copy because it describes where the link goes, not why access was
 * refused — that sentence is the backend's and is rendered verbatim beside it.
 * The namespace is named so the label says which namespace the link opens.
 */
export function requestAccessLabel(
  t: TranslateFn,
  namespaceId: string,
): string {
  return t('access-denied-bindle-link', { namespaceId });
}

/**
 * A link to the Bindle for the namespace a refusal named — the one thing that can
 * change the answer, which is what stops a 403 being a dead end.
 *
 * Shared because two surfaces render it: `AccessDeniedAlert` (every read page and
 * modal) and the submit flash on the string group page, whose failure arrives as
 * a Flashbar item rather than an alert. One component keeps their labels and
 * hrefs identical.
 */
const BindleAccessLinks: React.FC<BindleAccessLinkProps> = ({ link }) => {
  const { t } = useTranslation();

  return (
    <Link
      external
      externalIconAriaLabel={t('external-link-new-tab-aria')}
      href={link.href}
      data-testid={`access-denied-bindle-link-${link.namespaceId}`}
    >
      {requestAccessLabel(t, link.namespaceId)}
    </Link>
  );
};

export default BindleAccessLinks;
